import { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export type Pillar = 'search' | 'rate' | 'newbuild' | 'lab';

interface PillarContextValue {
  pillar: Pillar;
  isSearch: boolean;
  isRate: boolean;
  isNewbuild: boolean;
  isLab: boolean;
}

const PillarContext = createContext<PillarContextValue>({
  pillar: 'search',
  isSearch: true,
  isRate: false,
  isNewbuild: false,
  isLab: false,
});

export function usePillar() {
  return useContext(PillarContext);
}

export function PillarProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const p = location.pathname;
  const pillar: Pillar = p.startsWith('/newbuild')
    ? 'newbuild'
    : p.startsWith('/rate')
      ? 'rate'
      : p.startsWith('/search') || p.startsWith('/results') || p.startsWith('/listing')
        ? 'search'
        : p === '/' || p.startsWith('/lab') || p.startsWith('/login') || p.startsWith('/register') || p.startsWith('/verify-email') || p.startsWith('/reset-password')
          ? 'lab'
          : 'search';

  const value = useMemo<PillarContextValue>(() => ({
    pillar,
    isSearch: pillar === 'search',
    isRate: pillar === 'rate',
    isNewbuild: pillar === 'newbuild',
    isLab: pillar === 'lab',
  }), [pillar]);

  // Apply data-pillar attribute to root element for CSS theming
  useEffect(() => {
    document.documentElement.setAttribute('data-pillar', pillar);
    return () => {
      document.documentElement.removeAttribute('data-pillar');
    };
  }, [pillar]);

  return (
    <PillarContext.Provider value={value}>
      {children}
    </PillarContext.Provider>
  );
}
